import { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Orders as OrdersApi } from '../../api/client';
import { ExternalLink, DollarSign, User, Calendar, Package, MapPin, CreditCard, FileText } from 'lucide-react';

type Props = {
  etsyOrderId: string | null;
  onClose: () => void;
};

type OrderDetail = {
  id: number;
  etsy_order_id: string;
  buyer_email: string;
  name: string;
  total_amount: number;
  currency: string;
  status: string;
  created_at: number;
  raw: string | null;
};

export function OrderDetailModal({ etsyOrderId, onClose }: Props) {
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!etsyOrderId) { setOrder(null); return; }
    setLoading(true);
    OrdersApi.get(etsyOrderId).then(setOrder).catch(() => setOrder(null)).finally(() => setLoading(false));
  }, [etsyOrderId]);

  if (!etsyOrderId) return null;

  const parsed = order?.raw ? safeJson(order.raw) : null;
  const tx = parsed?.transactions ?? [];
  const shipments = parsed?.shipments ?? [];
  const address = parsed ? {
    line1: parsed.first_line ?? '',
    line2: parsed.second_line ?? '',
    city: parsed.city ?? '',
    state: parsed.state ?? '',
    zip: parsed.zip ?? '',
    country: parsed.country_iso ?? '',
  } : null;

  return (
    <Modal
      open={!!etsyOrderId}
      onClose={onClose}
      kicker="// order"
      title={loading ? 'Loading…' : order ? `Order #${order.etsy_order_id}` : 'Order'}
      width={620}
      footer={
        <>
          {order && (
            <Button
              variant="ghost"
              leftIcon={<ExternalLink size={12} />}
              onClick={() => window.open(`https://www.etsy.com/your/orders/sold?ref=mc_orders`, '_blank', 'noopener')}
            >
              Open in Etsy
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </>
      }
    >
      {loading ? (
        <div className="text-[12px] text-ink-500 py-8 text-center">Fetching from local cache…</div>
      ) : !order ? (
        <div className="text-[12px] text-ink-500 py-8 text-center">Order not found.</div>
      ) : (
        <div className="space-y-5">
          {/* Summary row */}
          <div className="grid grid-cols-3 gap-3">
            <Stat icon={DollarSign} label="Total" value={`${order.currency} ${Number(order.total_amount).toFixed(2)}`} />
            <Stat icon={Calendar} label="Date" value={new Date(order.created_at * 1000).toLocaleString()} />
            <div className="surface-soft p-3">
              <div className="kicker mb-1">status</div>
              <Badge tone={order.status === 'paid' ? 'success' : order.status === 'shipped' ? 'info' : order.status === 'completed' ? 'success' : 'warn'}>
                {order.status}
              </Badge>
            </div>
          </div>

          {/* Buyer */}
          <Section icon={User} title="Buyer">
            <div className="text-[13px] text-ink-800 font-medium">{order.name}</div>
            <div className="text-[12px] text-ink-500 mt-0.5">{order.buyer_email}</div>
          </Section>

          {/* Line items */}
          {tx.length > 0 && (
            <Section icon={Package} title={`Items (${tx.length})`}>
              <div className="space-y-1.5">
                {tx.map((t: any, i: number) => (
                  <div key={i} className="flex items-center justify-between gap-3 text-[12.5px] py-1.5 border-b border-cream-200 last:border-0">
                    <div className="min-w-0 flex-1">
                      <div className="text-ink-800 truncate font-medium">{t.title ?? 'Item'}</div>
                      <div className="kicker text-ink-400">qty {t.quantity ?? 1}</div>
                    </div>
                    <div className="tabular-nums text-ink-800">
                      {t.price?.amount ? `${(t.price.amount / (t.price.divisor || 100)).toFixed(2)} ${t.price.currency_code ?? order.currency}` : '—'}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Shipments */}
          {shipments.length > 0 && (
            <Section icon={MapPin} title="Shipments">
              <div className="space-y-2">
                {shipments.map((s: any, i: number) => (
                  <div key={i} className="surface-soft p-3">
                    <div className="flex items-center justify-between mb-1">
                      <Badge tone="info">{s.carrier_name ?? 'carrier'}</Badge>
                      {s.tracking_code && (
                        <code className="text-[11px] font-mono text-ink-600">{s.tracking_code}</code>
                      )}
                    </div>
                    <div className="text-[11.5px] text-ink-500">
                      {s.shipped_timestamp ? `Shipped ${new Date(s.shipped_timestamp * 1000).toLocaleDateString()}` : 'Pending'}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Shipping address */}
          {address && (address.line1 || address.city) && (
            <Section icon={MapPin} title="Ship to">
              <div className="text-[12.5px] text-ink-700 leading-relaxed">
                {address.line1}{address.line2 ? `, ${address.line2}` : ''}<br />
                {address.city}{address.state ? `, ${address.state}` : ''} {address.zip}<br />
                {address.country}
              </div>
            </Section>
          )}

          {/* Payment */}
          {parsed && (
            <Section icon={CreditCard} title="Payment">
              <div className="grid grid-cols-2 gap-3 text-[12.5px]">
                <div>
                  <div className="kicker mb-0.5">method</div>
                  <div className="text-ink-800">{parsed.payment_method ?? '—'}</div>
                </div>
                <div>
                  <div className="kicker mb-0.5">gross</div>
                  <div className="text-ink-800 tabular-nums">
                    {parsed.grandtotal ? `${(parsed.grandtotal.amount / (parsed.grandtotal.divisor || 100)).toFixed(2)} ${parsed.grandtotal.currency_code ?? ''}` : '—'}
                  </div>
                </div>
                <div>
                  <div className="kicker mb-0.5">subtotal</div>
                  <div className="text-ink-800 tabular-nums">
                    {parsed.subtotal ? `${(parsed.subtotal.amount / (parsed.subtotal.divisor || 100)).toFixed(2)} ${parsed.subtotal.currency_code ?? ''}` : '—'}
                  </div>
                </div>
                <div>
                  <div className="kicker mb-0.5">shipping</div>
                  <div className="text-ink-800 tabular-nums">
                    {parsed.total_shipping_cost ? `${(parsed.total_shipping_cost.amount / (parsed.total_shipping_cost.divisor || 100)).toFixed(2)} ${parsed.total_shipping_cost.currency_code ?? ''}` : '—'}
                  </div>
                </div>
              </div>
            </Section>
          )}

          {/* Buyer message */}
          {parsed?.message_from_buyer && (
            <Section icon={FileText} title="Buyer message">
              <div className="surface-soft p-3 text-[12.5px] text-ink-700 whitespace-pre-line italic">
                "{parsed.message_from_buyer}"
              </div>
            </Section>
          )}
        </div>
      )}
    </Modal>
  );
}

function Section({ icon: Icon, title, children }: { icon: any; title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-2">
        <Icon size={12} className="text-ink-400" />
        <div className="kicker">{title}</div>
      </div>
      {children}
    </div>
  );
}

function Stat({ icon: Icon, label, value }: { icon: any; label: string; value: string }) {
  return (
    <div className="surface-soft p-3">
      <div className="kicker mb-1">{label}</div>
      <div className="text-[13px] text-ink-800 flex items-center gap-1 font-medium tabular-nums">
        <Icon size={11} className="text-ink-400" />
        {value}
      </div>
    </div>
  );
}

function safeJson(s: string): any {
  try { return JSON.parse(s); } catch { return null; }
}
